import React, { ErrorInfo, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import {
  reportError,
  ErrorType,
} from '../../utilities/errorReporting/errorReporting';
import debounce from 'lodash/debounce';
import Risksystem3PublicAuthService from '../../services/risksystem3-public-auth-service';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  redirect: string;
  hasError: boolean;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  public state: ErrorBoundaryState = {
    redirect: '',
    hasError: false,
  };

  public static getDerivedStateFromError() {
    return { hasError: true };
  }

  // set the types for error  and info
  public componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, info);

    const isProduction = process.env.NODE_ENV === 'production';

    // Only send if in production
    if (isProduction) {
      this.debouncedSendError(error, info);
    }
  }

  private debouncedSendError = debounce(
    (error: Error, info: ErrorInfo) => {
      reportError(
        'ErrorBoundary caught an error',
        error,
        ErrorType.REACT_ERROR_BOUNDARY,
        {
          componentStack: info.componentStack,
        },
      );
    },
    3000, // We can up this if we want?
    { leading: true, trailing: false }, // Fires first and last
  );

  private handleLogout = () => {
    // Use the FastAPI logout service which handles all cleanup and redirect
    Risksystem3PublicAuthService.FastAPILogout();
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.redirect) {
      return <Navigate to="/" />;
    }
    if (this.state.hasError) {
      // Render error UI with recovery options
      return (
        <div className="flex h-[80vh] w-full flex-col items-center justify-center">
          <div className="flex max-w-[600px] flex-col items-center gap-6 p-8 text-center">
            <h2 className="mb-2 text-2xl font-semibold">
              Something went wrong
            </h2>
            <p className="text-gray-600">
              An error occurred, and a report has been submitted to the site
              administrator. Please try refreshing the page, or logout and log
              back in.
            </p>
            <p className="text-gray-600">
              If the error persists, please contact support.
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-4">
              <button
                onClick={this.handleLogout}
                className="inline-flex min-w-[150px] justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Logout
              </button>
              <button
                onClick={this.handleRefresh}
                className="inline-flex min-w-[150px] justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
